import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

const INPUT_STYLE = {
  width: '100%',
  height: '34px',
  borderRadius: '8px',
  padding: '0 10px',
  fontSize: '13px',
  outline: 'none',
  background: 'var(--input-bg, #fff)',
  border: '1px solid var(--border-subtle, #e6e6ef)',
  boxSizing: 'border-box',
};

/**
 * Edição de solicitante e contatos, com os mesmos campos da abertura da OS.
 */
export default function OrderDetailsSolicitanteEditModal({ edit }) {
  const inputRef = useRef(null);

  useEffect(() => {
    if (!edit?.open || edit.loading) return undefined;
    const t = window.setTimeout(() => {
      try { inputRef.current?.focus(); } catch { /* ignore */ }
    }, 40);
    return () => window.clearTimeout(t);
  }, [edit?.open, edit?.loading]);

  useEffect(() => {
    if (!edit?.open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !edit.saving) edit.closeModal?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [edit?.open, edit?.saving, edit?.closeModal]);

  if (!edit?.open) return null;

  const phoneInvalid = !edit.contatoOk;

  return createPortal(
    <div className="odm-address-edit-overlay" role="dialog" aria-modal="true" aria-labelledby="odm-solicitante-edit-title" data-order-details-portal>
      <div className="odm-address-edit-modal odm-solicitante-edit-modal">
        <h3 id="odm-solicitante-edit-title" className="odm-address-edit-title">Solicitante e contatos</h3>
        {edit.loading ? (
          <div className="odm-address-edit-loading">Carregando…</div>
        ) : (
          <div className="open-os-v2-mock-host odm-solicitante-edit-host">
            <div className="oosv2-page">
              <div className="oosv2-solicitante-grid">
                <div>
                  <div className="odm-sol-field-label">
                    <span>Solicitante</span>
                    <span className={`odm-sol-dot${edit.nomeOk ? ' is-ok' : ''}`} />
                  </div>
                  <input
                    ref={inputRef}
                    type="text"
                    className={`oosv2-solicitante-input oosv2-conf-input${edit.nomeOk ? '' : ' is-invalid'}`}
                    value={edit.nome}
                    onChange={(e) => edit.setNome(e.target.value.slice(0, 60))}
                    placeholder="Nome e sobrenome"
                    maxLength={60}
                    disabled={edit.saving}
                    style={INPUT_STYLE}
                  />
                </div>
                <div>
                  <div className="odm-sol-field-label">
                    <span>Contato</span>
                    <span className={`odm-sol-dot${edit.contatoOk ? ' is-ok' : ''}`} />
                  </div>
                  <div className="oosv2-contato-row">
                    <div className="oosv2-field-wrap">
                      <input
                        type="text"
                        className={`oosv2-conf-input${phoneInvalid ? ' is-invalid' : ''}`}
                        value={edit.phone}
                        onChange={(e) => edit.setPhone(e.target.value)}
                        placeholder="(XX)XXXXX-XXXX"
                        maxLength={14}
                        disabled={edit.saving}
                        style={INPUT_STYLE}
                      />
                    </div>
                    <div className="oosv2-fone-types">
                      {edit.tipos.map((tipo, i) => {
                        const key = tipo === 'WhatsApp' ? 'whats' : tipo === 'Fone2' ? 'fone2' : 'fone1';
                        const filled = String(edit.phones[key] || '').replace(/\D/g, '').length >= 10;
                        const on = edit.tipo === tipo;
                        return (
                          <button
                            key={tipo}
                            type="button"
                            className={`oosv2-fone-type${on ? ' is-on' : ''}${filled ? ' is-filled' : ''}`}
                            onClick={() => edit.setTipo(tipo)}
                            disabled={edit.saving}
                            style={{
                              padding: '0 9px',
                              height: '34px',
                              display: 'flex',
                              alignItems: 'center',
                              fontSize: '11px',
                              fontWeight: 700,
                              cursor: 'pointer',
                              border: 'none',
                              borderRight: i < edit.tipos.length - 1 ? '1px solid var(--border-subtle, #f0f0f5)' : 'none',
                              background: on ? '#6d3ef5' : 'var(--input-bg, #fff)',
                              color: on ? '#fff' : '#8a8a9e',
                            }}
                          >
                            {tipo}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="odm-sol-field-label">
                    <span>E-mail</span>
                    <span className={`odm-sol-hint${edit.draftOk ? '' : ' is-invalid'}`}>{edit.draftOk ? 'opcional' : 'e-mail inválido'}</span>
                  </div>
                  <div className={`oosv2-email-field${edit.draftOk ? '' : ' is-invalid'}`}>
                    <div className="oosv2-email-chips">
                      {edit.emails.map((addr) => (
                        <span key={addr} className="oosv2-email-chip">
                          <span className="oosv2-email-chip-text">{addr}</span>
                          <button type="button" className="oosv2-email-chip-x" onClick={() => edit.removeEmail(addr)} aria-label="Remover e-mail" disabled={edit.saving}>×</button>
                        </span>
                      ))}
                      <input
                        type="text"
                        className="oosv2-email-input"
                        value={edit.emailDraft}
                        onChange={(e) => edit.applyEmail(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            edit.commitEmail();
                          }
                          if (e.key === 'Backspace' && !edit.emailDraft && edit.emails.length) {
                            edit.removeEmail(edit.emails[edit.emails.length - 1]);
                          }
                        }}
                        onBlur={() => edit.commitEmail()}
                        placeholder="email@exemplo.com"
                        disabled={edit.saving}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {edit.feedback?.type === 'error' ? <p className="odm-address-edit-error">{edit.feedback.message}</p> : null}
        <div className="odm-address-edit-actions">
          <button type="button" className="user-form-link-btn user-form-footer-btn" onClick={edit.closeModal} disabled={edit.saving}>Cancelar</button>
          <button type="button" className="user-form-profile-save-btn user-form-footer-btn" onClick={edit.confirm} disabled={!edit.canConfirm}>
            {edit.saving ? 'Salvando…' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
